import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import type { ISiteSlim } from "App/Modules/Models";

import { Button } from "@material-ui/core";
import { Skeleton } from "@material-ui/lab";
import { faHouse, faFilter, faChevronRight } from "@fortawesome/pro-solid-svg-icons";
import { faHorizontalRule } from "@fortawesome/pro-light-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { SitesLinkFilter } from "./SitesLinkFilter";
import "./_list-section.scss";

type SitesLinkListProps = {
    sites: ISiteSlim[];
    isLoading: boolean;
    isSitesLoading: boolean;
};

export const SitesLinkList = (props: SitesLinkListProps) => {
    const { sites, isLoading, isSitesLoading } = props;
    const [pageNumber, setPageNumber] = useState(1);
    const [siteFilter, setSiteFilter] = useState("");
    const [stateFilter, setStateFilter] = useState([]);
    const [filterIsOpen, setFilterIsOpen] = useState(false);
    const { t } = useTranslation();

    const increasePageNumber = () => {
        setPageNumber(pageNumber + 1);
    };

    const handleDisplaySearchFilter = (event: React.MouseEvent) => {
        event.preventDefault();
        setFilterIsOpen(!filterIsOpen);
    };
    const findStateModifier = (site) => {
        if (site.siteState === "Normal") {
            return "state-tag--approve";
        }
        if (site.siteState === "Maintenance") {
            return "state-tag--info";
        }
        if (site.siteState === "Not started") {
            return "state-tag--warning";
        }
        return "";
    };
    const filterFunc = (site: ISiteSlim) => {
        if (!siteFilter && stateFilter.length === 0) {
            return true;
        }
        const hasCorrectState =
            stateFilter.length === 0 ||
            stateFilter.some(
                (filterState) =>
                    filterState.value.toLowerCase() === (site.siteState as unknown as string).toLowerCase(),
            );
        const siteName = site.name.toLowerCase();
        const partnerName = site.partner.name.toLowerCase();
        return (
            (siteName.indexOf(siteFilter.toLowerCase()) > -1 || partnerName.indexOf(siteFilter.toLowerCase()) > -1) &&
            hasCorrectState
        );
    };
    const onFilterChange = (searchFilter: string, siteStates: any[]) => {
        setSiteFilter(searchFilter);
        setStateFilter(siteStates);
    };

    const filteredSites = (sites || []).filter((site) => filterFunc(site));

    return isLoading && isSitesLoading ? (
        <div className="list-section">
            <h2 className="title title--h2">{t("BeforeLogin.Site.Sites")}</h2>
            <ul className="entity-list">
                {Array.from(new Array(2)).map((d, i) => (
                    <li key={i} className="entity-list__item">
                        <div className="entity-list__item-link entity-list__item-link--button">
                            <div className="entity-list__item-icon entity-list__item-icon--skeleton">
                                <Skeleton variant="circle" animation="wave" width="30px" height="30px" />
                            </div>
                            <div className="entity-list__item-panel">
                                <div className="entity-list__item-value">
                                    <Skeleton variant="text" animation="wave" height="16px" width="70%" />
                                    <Skeleton variant="text" animation="wave" height="22px" width="77px" />
                                </div>
                            </div>
                        </div>
                    </li>
                ))}
            </ul>
        </div>
    ) : (
        <React.Fragment>
            {sites ? (
                <div className="list-section">
                    <div className="list-section__title-heading">
                        <h2 className="title title--h2">{t("BeforeLogin.Site.Sites")}</h2>
                        {filteredSites?.length < sites?.length ? (
                            <span className="caption">
                                <FontAwesomeIcon icon={faHorizontalRule} className="list-section__title-icon" />
                                {`${t("Gui.Manage.ShowingOfTotal", {
                                    showCount: filteredSites?.length,
                                    totalCount: sites?.length,
                                })}`}
                            </span>
                        ) : (
                            <span className="caption">
                                <FontAwesomeIcon icon={faHorizontalRule} className="list-section__title-icon" />
                                {`${sites?.length || 0} ${t("Gui.Common.AlarmCentralValue.Found")}`}
                            </span>
                        )}
                    </div>
                    <div className="list-section__buttons">
                        <Button
                            size="small"
                            color="default"
                            className={
                                filterIsOpen
                                    ? "list-section__filter-btn list-section__filter-btn--active"
                                    : "list-section__filter-btn"
                            }
                            onClick={handleDisplaySearchFilter}
                            startIcon={<FontAwesomeIcon icon={faFilter} className="list-section__button-icon" />}
                        >
                            {t("Gui.Common.Filter")}
                        </Button>
                    </div>

                    {sites ? <SitesLinkFilter sites={sites} active={filterIsOpen} onChange={onFilterChange} /> : null}
                    <ul className="entity-list">
                        {filteredSites.length === 0 ? (
                            <p className="paragraph paragraph--italic">{t("Gui.Common.AlarmCentralSites.NotFound")}</p>
                        ) : null}
                        {filteredSites.map((site, siteIndex) => {
                            if (siteIndex >= pageNumber * 10) {
                                return null;
                            }
                            return (
                                <li className="entity-list__item" key={site.id}>
                                    <a href={`/#!/sites/${site.id}`} className="entity-list__item-link">
                                        <div className="entity-list__item-icon">
                                            <FontAwesomeIcon icon={faHouse} />
                                        </div>
                                        <div className="entity-list__item-panel">
                                            <div className="entity-list__item-value">
                                                <p className="paragraph paragraph--bold">
                                                    {site.name}&nbsp;
                                                    <span className="caption">- {site.partner.name}</span>
                                                </p>
                                                <p className={`state-tag ${findStateModifier(site)}`}>
                                                    {t(`Enums.SiteState.Enum${site.siteState}`)}
                                                </p>
                                            </div>
                                        </div>
                                        <div className="entity-list__item-arrow">
                                            <FontAwesomeIcon icon={faChevronRight} />
                                        </div>
                                    </a>
                                </li>
                            );
                        })}
                    </ul>
                    {pageNumber * 10 <= sites?.length ? (
                        <Button
                            variant="contained"
                            size="large"
                            color="primary"
                            className="list-section__show-more"
                            onClick={() => increasePageNumber()}
                        >
                            {t("Gui.Buttons.Show.More")}
                        </Button>
                    ) : null}
                </div>
            ) : null}
        </React.Fragment>
    );
};
